import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, MapPin, Clock } from "lucide-react";

const DOT = {
  teal: "bg-teal-400 text-white",
  pink: "bg-pink-500 text-white",
  green: "bg-emerald-500 text-white",
  yellow: "bg-yellow-400 text-emerald-900",
};
const LABEL = {
  teal: "text-teal-600",
  pink: "text-pink-500",
  green: "text-emerald-600",
  yellow: "text-yellow-600",
};
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const fmtLong = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

export const CampCalendar = ({ events = [] }) => {
  const initial = events.length ? events[0].date : new Date().toISOString().slice(0, 10);
  const [iy, im] = [Number(initial.slice(0, 4)), Number(initial.slice(5, 7)) - 1];
  const [view, setView] = useState({ year: iy, month: im });
  const [selected, setSelected] = useState(null);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => (map[e.date] = e));
    return map;
  }, [events]);

  const monthEvents = useMemo(
    () =>
      events
        .filter((e) => {
          const [y, m] = e.date.split("-").map(Number);
          return y === view.year && m - 1 === view.month;
        })
        .sort((a, b) => a.date.localeCompare(b.date)),
    [events, view]
  );

  const firstDay = new Date(view.year, view.month, 1).getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const changeMonth = (delta) => {
    setSelected(null);
    setView((v) => {
      let m = v.month + delta;
      let y = v.year;
      if (m < 0) { m = 11; y--; }
      if (m > 11) { m = 0; y++; }
      return { year: y, month: m };
    });
  };

  const dateStr = (d) =>
    `${view.year}-${String(view.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <section id="calendar" data-testid="calendar-section" className="bg-yellow-50 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-center font-display font-bold text-teal-600 text-4xl sm:text-5xl mb-12">
          Where We'll Be This Week
        </h2>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calendar grid */}
          <div data-testid="calendar-grid" className="bg-white rounded-[2rem] p-6 sm:p-8 shadow-pop-teal border-4 border-teal-100">
            <div className="flex items-center justify-between mb-6">
              <button data-testid="cal-prev" onClick={() => changeMonth(-1)} className="text-teal-500 hover:text-pink-500 hover:scale-110 transition">
                <ChevronLeft className="w-8 h-8" />
              </button>
              <h3 className="font-display font-bold text-2xl text-emerald-800 uppercase tracking-wide">
                {MONTHS[view.month]} {view.year}
              </h3>
              <button data-testid="cal-next" onClick={() => changeMonth(1)} className="text-teal-500 hover:text-pink-500 hover:scale-110 transition">
                <ChevronRight className="w-8 h-8" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center mb-2">
              {DOW.map((d) => (
                <div key={d} className="font-display font-bold text-teal-600 text-xs sm:text-sm uppercase">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 text-center">
              {cells.map((d, i) => {
                if (d === null) return <div key={`e${i}`} />;
                const ds = dateStr(d);
                const ev = eventsByDate[ds];
                const isSel = selected === ds;
                return (
                  <div key={ds} className="aspect-square flex items-center justify-center">
                    {ev ? (
                      <button
                        data-testid={`cal-day-${ds}`}
                        onClick={() => setSelected(ds)}
                        className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full font-display font-bold text-sm sm:text-base flex items-center justify-center transition-all hover:scale-110 ${DOT[ev.color] || DOT.teal} ${isSel ? "ring-4 ring-pink-300 scale-110" : ""}`}
                      >
                        {d}
                      </button>
                    ) : (
                      <span className="text-emerald-900/70 font-semibold text-sm sm:text-base">{d}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Side panel */}
          <div data-testid="calendar-side-panel" className="bg-[#FFFDF2] rounded-[2rem] p-6 sm:p-8 shadow-pop border-4 border-pink-100">
            <p className="font-display font-semibold text-pink-500 text-lg mb-5">
              Click a highlighted date, or browse the list below!
            </p>
            <div className="space-y-3">
              {monthEvents.length === 0 && (
                <p className="text-emerald-800 font-medium">No camp dates scheduled this month. Check another month!</p>
              )}
              {monthEvents.map((e) => {
                const isSel = selected === e.date;
                return (
                  <div
                    key={e.id || e.date}
                    data-testid={`event-row-${e.date}`}
                    className={`rounded-2xl p-4 transition-all ${isSel ? "bg-pink-50 ring-2 ring-pink-300" : "bg-white"}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-1 w-3 h-3 rounded-full shrink-0 ${DOT[e.color] || DOT.teal}`} />
                      <div className="min-w-0">
                        <p className={`font-display font-bold text-sm uppercase ${LABEL[e.color] || LABEL.teal}`}>
                          {fmtLong(e.date)}
                        </p>
                        <p className="font-display font-bold text-emerald-900">{e.library_name}</p>
                        <p className="text-emerald-700 text-sm flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3.5 h-3.5 shrink-0" /> {e.address}
                        </p>
                        <p className="text-emerald-700 text-sm flex items-center gap-1 mt-0.5">
                          <Clock className="w-3.5 h-3.5 shrink-0" /> {e.time}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
